import { Box, TextField, Typography } from '@atgfr8/atg-ui-tailwind';
import { CheckCircleIcon } from '@atgfr8/icons';
import { BasicModal, Button } from '@components';
import { TextArea } from '@components/TextField/TextArea';
import {
  creditIncrease,
  creditRequestStore,
} from '@store/customers/customerDetails';
import { closeModal } from '@store/modals';
import { orderStore } from '@store/orders';
import { userStore } from '@store/user/store';
import { DateTime } from 'luxon';
import { Show, createSignal } from 'solid-js';

export const CreditIncreaseRequestModalId = 'CreditIncreaseRequestModal';

const [acceptedLiability, setAcceptedLiabilityInternal] = createSignal(false);

export const setAcceptedLiability = (value: boolean) =>
  setAcceptedLiabilityInternal(value);

export type CreditIncreaseRequestRecord = {
  amount: number;
  reason: string;
  requestedByUserName: string;
  requestedAt: string;
};

type StoredCreditIncreaseRequest = {
  orderId: number;
  record: CreditIncreaseRequestRecord;
};

const [creditIncreaseRequest, setCreditIncreaseRequest] =
  createSignal<StoredCreditIncreaseRequest | null>(null);

export const creditIncreaseRequestForCurrentOrder =
  (): CreditIncreaseRequestRecord | null => {
    const entry = creditIncreaseRequest();
    if (!entry || entry.orderId !== orderStore.order.id) {
      return null;
    }
    return entry.record;
  };

export const resetCreditIncreaseRequest = () => setCreditIncreaseRequest(null);

export const CreditIncreaseRequestModal = () => {
  const [amount, setAmount] = createSignal<number>(0);
  const [reason, setReason] = createSignal('');

  const isAmountValid = () => amount() > 0;

  const reset = () => {
    setAmount(0);
    setReason('');
    setAcceptedLiabilityInternal(false);
  };

  const handleClose = () => {
    reset();
    closeModal(CreditIncreaseRequestModalId);
  };

  const preventingNonNumeric = (e: KeyboardEvent) => {
    if (e.key === 'e' || e.key === '-' || e.key === '+') {
      e.preventDefault();
    }
  };

  const handleSubmit = async () => {
    const customerId = orderStore.order.customerId;
    if (customerId == null || !isAmountValid()) {
      return;
    }

    const submittedAmount = amount();
    const submittedReason = reason();

    await creditIncrease(
      {
        amount: submittedAmount,
        reason: submittedReason,
        customerId: String(customerId),
      },
      () => {
        setCreditIncreaseRequest({
          orderId: orderStore.order.id,
          record: {
            amount: submittedAmount,
            reason: submittedReason,
            requestedByUserName: userStore.user.name,
            requestedAt: DateTime.now().toISO(),
          },
        });
        reset();
        closeModal(CreditIncreaseRequestModalId);
      },
    );
  };

  return (
    <BasicModal
      id={CreditIncreaseRequestModalId}
      customizedHeaderContent={
        <span class="text-primary font-semibold">
          Credit Limit Acknowledgement
        </span>
      }
      width="600px"
      footer={false}
      showClose
      onClose={handleClose}
    >
      <Box class="flex flex-col gap-3 pt-2">
        <Show when={acceptedLiability()}>
          <Box class="flex items-center gap-2 rounded-md border-0 bg-[#EDF7ED] px-3 py-2">
            <CheckCircleIcon
              style={{ 'font-size': '20px', color: '#2e7d32' }}
            />
            <Typography variant="body2">
              You have accepted liability for this order&apos;s negative credit
              balance.
            </Typography>
          </Box>
        </Show>
        <Show when={!acceptedLiability()}>
          <Typography variant="body1">
            You’ve chosen not to accept liability. Submit a credit increase
            request to the Credit Team to proceed with this order.
          </Typography>
        </Show>
        <Box class="flex flex-col gap-1 rounded-md bg-[#ECEFF1] p-4">
          <Typography variant="body2" class="text-primary font-medium!">
            Request Additional Credit
          </Typography>
          <Show when={acceptedLiability()}>
            <Typography variant="caption" class="text-[#666666]">
              Would you like to request additional credit for this customer?
            </Typography>
          </Show>
          <TextField
            label="Amount"
            type="number"
            placeholder="Enter Amount here"
            value={amount()}
            onChange={(e: unknown) => setAmount(Number(e as number))}
            startIcon="$"
            maxLength={10}
            onKeyPress={preventingNonNumeric}
            dataTestId="creditIncreaseRequest-amount-textInput"
          />
          <div class="[&_textarea]:bg-white">
            <TextArea
              label="Reason/Comment"
              value={reason()}
              onInput={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>
        </Box>
        <Box class="flex items-center justify-between gap-2 mt-1">
          <Show
            when={acceptedLiability()}
            fallback={
              <Button
                variant="outlined"
                label="Cancel"
                onClick={handleClose}
                dataTestId="creditIncreaseRequest-cancel-button"
              />
            }
          >
            <Button
              variant="outlined"
              label="Continue to Order"
              onClick={handleClose}
              dataTestId="creditIncreaseRequest-continueToOrder-button"
            />
          </Show>
          <Button
            variant="contained"
            color="primary"
            label="Submit Request"
            disabled={!isAmountValid()}
            isLoading={creditRequestStore.isLoading}
            onClick={handleSubmit}
            dataTestId="creditIncreaseRequest-submit-button"
          />
        </Box>
      </Box>
    </BasicModal>
  );
};
